import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, X, MessageSquare, Send, History } from "lucide-react";
import AIEmailModal from "@/components/AIEmailModal";
import { SERVICE_TYPES, TASK_TYPES, TASK_BILLING, STATUSES, STATUS_LABEL } from "@/pages/Tasks";
import { useAuth } from "@/context/AuthContext";

export default function TaskDrawer({ taskId, clients, users, onClose }) {
  const { user: me } = useAuth();
  const canReassignBy = me?.role === "admin" || me?.role === "manager";
  const [task, setTask] = useState(null);
  const [audit, setAudit] = useState([]);
  const [comment, setComment] = useState("");
  const [showAI, setShowAI] = useState(false);
  const [tab, setTab] = useState("comments");
  const [error, setError] = useState("");

  async function load() {
    const [t, a] = await Promise.all([
      api.get(`/tasks/${taskId}`),
      api.get(`/tasks/${taskId}/audit`),
    ]);
    setTask(t.data); setAudit(a.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [taskId]);

  if (!task) return null;

  const client = clients.find((c) => c.id === task.client_id);
  const assignedBy = users.find((u) => u.id === task.assigned_by_id);

  async function update(patch) {
    setError("");
    try {
      const r = await api.put(`/tasks/${taskId}`, patch);
      setTask(r.data);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Update failed");
    }
  }
  async function addComment() {
    if (!comment.trim()) return;
    await api.post(`/tasks/${taskId}/comments`, { text: comment });
    setComment("");
    load();
  }
  async function copyDraft(draft) {
    try {
      await navigator.clipboard.writeText(draft);
    } catch (e) {
      // silent — nothing to log
    }
  }

  async function changeTaskBilling(next) {
    if (next === "additional") {
      const currentAmt = task.billing_amount || 0;
      const val = window.prompt("Additional billing amount required (£):", currentAmt || "");
      const amt = parseFloat(val);
      if (!amt || amt <= 0) {
        setError("A positive billing amount is required for Additional Billing.");
        return;
      }
      await update({ task_billing: next, billing_amount: amt });
    } else {
      await update({ task_billing: next });
    }
  }

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="drawer p-0" data-testid="task-drawer">
        <div className="sticky top-0 bg-[var(--bg)] border-b border-[var(--line)] px-8 py-5 flex items-center justify-between">
          <div className="overline">Task #{taskId.slice(-6)}</div>
          <button data-testid="drawer-close" onClick={onClose} className="text-[var(--ink-2)] hover:text-[var(--ink)]"><X size={16} strokeWidth={1.5} /></button>
        </div>
        <div className="p-8">
          <input
            data-testid="drawer-name"
            className="font-display text-3xl bg-transparent border-none w-full focus:outline-none mb-2"
            defaultValue={task.name}
            onBlur={(e) => e.target.value !== task.name && update({ name: e.target.value })}
          />
          <textarea
            data-testid="drawer-desc"
            className="w-full bg-transparent border-none text-sm text-[var(--ink-2)] focus:outline-none resize-none mb-6"
            rows={2}
            defaultValue={task.description}
            onBlur={(e) => e.target.value !== task.description && update({ description: e.target.value })}
          />

          <div className="card-flat p-5 grid grid-cols-2 gap-4 mb-6">
            <Meta label="Client" value={client?.client_name || (task.all_clients ? "All Clients" : "—")} />
            <Meta label="Assigned By">
              {canReassignBy ? (
                <select
                  data-testid="drawer-assigned-by"
                  value={task.assigned_by_id || ""}
                  onChange={(e) => update({ assigned_by_id: e.target.value })}
                  className="input text-xs py-1"
                >
                  <option value="">— Unassigned</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              ) : (
                <div className="text-sm" data-testid="drawer-assigned-by-readonly">{assignedBy?.name || "—"}</div>
              )}
            </Meta>
            <Meta label="Assign To">
              <select
                data-testid="drawer-assignee"
                value={task.assignee_id || ""}
                onChange={(e) => update({ assignee_id: e.target.value })}
                className="input text-xs py-1"
              >
                <option value="">— Unassigned</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Meta>
            <Meta label="Service Type">
              <select data-testid="drawer-service-type" value={task.service_type || "Other Business Support"} onChange={(e) => update({ service_type: e.target.value })} className="input text-xs py-1">
                {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Meta>
            <Meta label="Task Type">
              <select data-testid="drawer-task-type" value={task.task_type || ""} onChange={(e) => update({ task_type: e.target.value })} className="input text-xs py-1">
                <option value="">—</option>
                {TASK_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Meta>
            <Meta label="Priority">
              <select data-testid="drawer-priority" value={task.priority} onChange={(e) => update({ priority: e.target.value })} className="input text-xs py-1">
                {["low", "medium", "high", "urgent"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </Meta>
            <Meta label="Start Date">
              <input
                data-testid="drawer-start"
                type="date"
                defaultValue={(task.start_date || "").slice(0, 10)}
                onBlur={(e) => e.target.value !== (task.start_date || "").slice(0, 10) && update({ start_date: e.target.value })}
                className="input text-xs py-1"
              />
            </Meta>
            <Meta label="Deadline">
              <input
                data-testid="drawer-deadline"
                type="date"
                defaultValue={(task.deadline || "").slice(0, 10)}
                onBlur={(e) => e.target.value !== (task.deadline || "").slice(0, 10) && update({ deadline: e.target.value })}
                className="input text-xs py-1"
              />
            </Meta>
            <Meta label="Completion Date">
              <input
                data-testid="drawer-completion"
                type="date"
                defaultValue={(task.completion_date || "").slice(0, 10)}
                onBlur={(e) => e.target.value !== (task.completion_date || "").slice(0, 10) && update({ completion_date: e.target.value })}
                className="input text-xs py-1"
              />
            </Meta>
            <Meta label="Status">
              <select data-testid="drawer-status" value={task.status} onChange={(e) => update({ status: e.target.value })} className="input text-xs py-1">
                {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </Meta>
          </div>

          <div className="card-flat p-5 mb-6" data-testid="drawer-task-billing">
            <div className="label-overline">Task Billing</div>
            <div className="flex flex-col gap-2 mt-2">
              {TASK_BILLING.map((b) => (
                <label key={b.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="drawer-task-billing"
                    value={b.key}
                    data-testid={`drawer-billing-${b.key}`}
                    checked={task.task_billing === b.key}
                    onChange={(e) => changeTaskBilling(e.target.value)}
                  />
                  {b.label}
                  {b.key === "additional" && task.task_billing === "additional" && task.billing_amount != null && (
                    <span className="ml-2 font-mono-data text-[var(--warn)]">· £{Number(task.billing_amount).toLocaleString()}</span>
                  )}
                </label>
              ))}
            </div>
            {task.task_billing === "additional" && (
              <div className="mt-3">
                <label className="label-overline">Billing Amount (£)</label>
                <input
                  data-testid="drawer-billing-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue={task.billing_amount || ""}
                  onBlur={(e) => {
                    const v = parseFloat(e.target.value);
                    if (v > 0 && v !== task.billing_amount) update({ billing_amount: v });
                  }}
                  className="input"
                />
              </div>
            )}
          </div>

          {error && <div className="text-sm text-[var(--err)] mb-4" data-testid="drawer-error">{error}</div>}

          <div className="flex gap-2 mb-6">
            {(me?.role === "admin" || me?.ai_enabled !== false) && (
              <button data-testid="ai-email-btn" onClick={() => setShowAI(true)} className="btn-primary flex items-center gap-2">
                <Sparkles size={14} strokeWidth={1.5} /> Generate email
              </button>
            )}
            {me?.role === "admin" && (
              task.archived ? (
                <button data-testid="unarchive-btn" onClick={async () => { await api.patch(`/tasks/${taskId}/unarchive`); load(); }} className="btn-ghost flex items-center gap-2">
                  Unarchive
                </button>
              ) : (
                <button data-testid="archive-btn" onClick={async () => {
                  if (!window.confirm("Archive this task? It will be hidden from default views. You can unarchive from the Show archived filter.")) return;
                  await api.patch(`/tasks/${taskId}/archive`);
                  onClose();
                }} className="btn-ghost flex items-center gap-2 text-[var(--ink-2)]">
                  Archive
                </button>
              )
            )}
          </div>

          <div className="flex gap-4 border-b border-[var(--line)] mb-4">
            <TabBtn active={tab === "comments"} onClick={() => setTab("comments")} icon={MessageSquare} label="Comments" testid="tab-comments" />
            <TabBtn active={tab === "audit"} onClick={() => setTab("audit")} icon={History} label="Audit" testid="tab-audit" />
          </div>

          {tab === "comments" && (
            <div>
              <div className="space-y-3 mb-4">
                {(task.comments || []).map((c) => (
                  <div key={c.id} className="card-flat p-3" data-testid={`comment-${c.id}`}>
                    <div className="text-xs text-[var(--ink-2)] flex justify-between mb-1">
                      <span>{c.user_name}</span>
                      <span className="font-mono-data">{c.at.slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div className="text-sm">{c.text}</div>
                  </div>
                ))}
                {(!task.comments || task.comments.length === 0) && <div className="text-sm text-[var(--ink-2)]">No comments yet.</div>}
              </div>
              <div className="flex gap-2">
                <input data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)} className="input flex-1" placeholder="Add a comment…" />
                <button data-testid="comment-submit" onClick={addComment} className="btn-primary"><Send size={14} strokeWidth={1.5} /></button>
              </div>
            </div>
          )}

          {tab === "audit" && (
            <div className="border-l-2 border-[var(--line)] pl-4 ml-2">
              {audit.map((a) => (
                <div key={a.id} className="relative pb-4" data-testid={`audit-${a.id}`}>
                  <span className="absolute -left-[22px] top-1 w-2 h-2 rounded-full bg-[var(--ink)]" />
                  <div className="text-xs font-mono-data text-[var(--ink-2)]">{a.at.slice(0, 16).replace("T", " ")}</div>
                  <div className="text-sm"><span className="capitalize">{a.action}</span> by {a.user_email}</div>
                </div>
              ))}
              {audit.length === 0 && <div className="text-sm text-[var(--ink-2)]">No audit entries.</div>}
            </div>
          )}
        </div>
        {showAI && (
          <AIEmailModal
            taskId={taskId}
            onClose={() => setShowAI(false)}
            onLog={async (draft) => { await copyDraft(draft); setShowAI(false); }}
          />
        )}
      </div>
    </>
  );
}

function Meta({ label, value, children }) {
  return (
    <div>
      <div className="label-overline">{label}</div>
      {children || <div className="text-sm">{value}</div>}
    </div>
  );
}
function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`flex items-center gap-2 pb-3 text-sm border-b-2 -mb-px transition ${active ? "border-[var(--ink)] text-[var(--ink)]" : "border-transparent text-[var(--ink-2)] hover:text-[var(--ink)]"}`}>
      <Icon size={14} strokeWidth={1.5} /> {label}
    </button>
  );
}
